// "Choose pack" on a meal card (issue 255): a pop-up with the product's packs for the customer's
// menu, a quantity and Add to cart, added in place (cck-cart-add.js); "See the meal" goes to the
// full page. Each card carries its packs as JSON (snippets/cck-meal-card.liquid). Without this
// script, "Choose pack" is a plain link to the product page. The pincode gate (cck-region.js)
// still holds an add when no menu is chosen yet.
import { addInPlace, paintInCart } from "@cck/cart-add";
import { gate } from "@cck/pack-rule";

/** @typedef {{ id: number, title: string, price: string, each: string, available: boolean, needsPack?: boolean, pack?: boolean }} Pack */
/** @typedef {{ title: string, url: string, image: string | null, selected: number, kind?: string, menu?: string, variants: Pack[] }} Packs */

const esc = (/** @type {unknown} */ s) =>
  String(s ?? "").replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

/** The packs' and stepper's styles, from the product page and the calculator, once. */
let styled = false;
function loadStyles() {
  if (styled) return;
  styled = true;
  const tag = document.querySelector('script[src*="cck-quick-add"]');
  for (const href of (tag?.getAttribute("data-css") ?? "").split(" ").filter(Boolean)) {
    if (document.querySelector(`link[href="${href}"]`)) continue;
    document.head.append(
      Object.assign(document.createElement("link"), { rel: "stylesheet", href }),
    );
  }
}

/** @type {HTMLDialogElement | null} */
let dialog = null;
function theDialog() {
  if (dialog) return dialog;
  dialog = document.createElement("dialog");
  dialog.className = "cck-quick-add";
  dialog.setAttribute("aria-labelledby", "cck-quick-add-title");
  // A click on the backdrop (the dialog itself, outside its box) closes it.
  dialog.addEventListener("click", (e) => {
    if (e.target === dialog) dialog?.close();
  });
  document.body.append(dialog);
  return dialog;
}

function open(/** @type {Packs} */ p) {
  loadStyles();
  const d = theDialog();
  const selected =
    p.variants.find((v) => v.id === p.selected && v.available) ??
    p.variants.find((v) => v.available) ??
    p.variants[0];
  d.innerHTML = `
    <form method="post" action="/cart/add" class="cck-quick-add__form" data-cck-quick-add-form data-cck-title="${esc(p.title)}" data-cck-handle="${esc(p.url.split("/").pop() ?? "")}">
      <input type="hidden" name="form_type" value="product">
      <div class="cck-quick-add__head">
        ${p.image ? `<img src="${esc(p.image)}" alt="" width="64" height="64">` : ""}
        <h2 class="cck-quick-add__title" id="cck-quick-add-title">${esc(p.title)}</h2>
        <button type="button" class="cck-round-button cck-quick-add__close" aria-label="Close" data-cck-close>×</button>
      </div>
      <fieldset class="cck-packs">
        <legend class="cck-calc__label">Pack</legend>
        ${p.variants
          .map(
            (v) => `
          <label class="cck-pack">
            <input type="radio" name="id" value="${v.id}" data-price="${esc(v.price)}"
              ${v.id === selected?.id ? "checked" : ""} ${v.available ? "" : "disabled"}
              data-cck-available="${v.available}" ${v.needsPack ? "data-cck-needs-pack" : ""} ${v.pack ? "data-cck-pack" : ""}>
            <span class="cck-pack__name">${esc(v.title)}</span>
            <span class="cck-pack__price">${esc(v.price)}</span>
            ${v.each ? `<span class="cck-pack__each">${esc(v.each)}</span>` : ""}
          </label>`,
          )
          .join("")}
      </fieldset>
      ${
        p.variants.some((v) => v.needsPack)
          ? `<p class="cck-pack-reason" data-cck-pack-reason hidden>${
              p.kind === "broth"
                ? "Single broths travel with a meal pack. Add a meal pack first, or take the Pack of 12, which travels on its own."
                : "Treats travel with a meal pack, because every national order flies frozen. Add a meal pack first."
            }</p>`
          : ""
      }
      <div class="cck-product__buy">
        <div class="cck-calc__stepper" role="group" aria-label="Quantity">
          <button type="button" class="cck-calc__step" aria-label="One fewer" data-step="-1">−</button>
          <input class="cck-product__qty" type="number" name="quantity" value="1" min="1" max="20" inputmode="numeric" aria-label="Quantity">
          <button type="button" class="cck-calc__step" aria-label="One more" data-step="1">+</button>
        </div>
        <button type="submit" class="cck-amber-button cck-amber-button--large" ${selected?.available ? "" : "disabled"}>
          Add to cart · <span data-price>${esc(selected?.price ?? "")}</span>
        </button>
        ${p.menu && p.variants.some((v) => v.needsPack) ? `<a class="cck-amber-button cck-amber-button--large" href="${esc(p.menu)}" data-cck-pack-link hidden>Add a meal pack first</a>` : ""}
      </div>
      <a class="cck-text-link cck-quick-add__more" href="${esc(p.url)}">See the whole meal</a>
    </form>`;
  const form = /** @type {HTMLFormElement} */ (d.querySelector("form"));
  const price = form.querySelector("[data-price]");
  const qty = /** @type {HTMLInputElement} */ (form.querySelector('input[name="quantity"]'));
  form.addEventListener("change", (e) => {
    const t = e.target;
    if (t instanceof HTMLInputElement && t.name === "id" && price)
      price.textContent = t.dataset.price ?? "";
  });
  for (const b of form.querySelectorAll("[data-step]")) {
    b.addEventListener("click", () => {
      const next = Number(qty.value || 1) + Number(b.getAttribute("data-step"));
      qty.value = String(Math.min(20, Math.max(1, next)));
    });
  }
  d.querySelector("[data-cck-close]")?.addEventListener("click", () => d.close());
  addInPlace(form, () => d.close());
  // Issue 459: which of these packs are in the cart already.
  paintInCart(d);
  // Issue 408: national add-ons' singles wait for a pack in the cart.
  gate(form);
  d.showModal();
  /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]:checked'))?.focus();
}

document.addEventListener("click", (e) => {
  if (!(e.target instanceof Element) || e.defaultPrevented) return;
  const link = e.target.closest("a[data-cck-quick-add]");
  // A new tab or window (Ctrl, Cmd, Shift, middle click) still opens the page.
  if (
    !link ||
    (e instanceof MouseEvent && (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0))
  )
    return;
  const json = link.closest(".cck-meal-card")?.querySelector("script[data-cck-packs]");
  if (!json) return;
  try {
    const packs = /** @type {Packs} */ (JSON.parse(json.textContent ?? ""));
    e.preventDefault();
    open(packs);
  } catch {
    // Unreadable data: the link opens the product page as usual.
  }
});
